import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getCartAPI,
  getCartTotalAPI,
} from "../../../../Store/Action/cartAction";
import { getLoggedInUserDetailsAPI } from "../../../../Store/Action/UserAction";
import "./styles.css";
import Icons from "../../../../Config/Images";
import AddAddressModal from "../AddAddressModal";
import { getAddressLists } from "../../../../Store/Action/addressAction";
import AddressCard from "../AddressCard";
import { useNavigate } from "react-router-dom";
import { differenceInMinutes } from "date-fns";
import { setLiveRates } from "../../../../Store/Slice/liveRateSlice";
import { getLiveRateAPI } from "../../../../Store/Action/globalAction";
import {
  applyWalletCheckAPI,
  latestWalletTransactionUrl,
} from "../../../../Store/Action/walletAction";
import {
  resetWalletFlag,
  setFinalPayableAmount,
  setWalletFlag,
} from "../../../../Store/Slice/walletSlice";
import { resolveDateFormat } from "@mui/x-date-pickers/internals/utils/date-utils";
import { numberFormatter } from "../../../../Utils/orderUtils";

const RightSideCart = ({
  isSameAddress,
  setIsSameAddress,
  totalAmount,
  refreshAddressInCart,
  // totalAmountData
  // addressDataByLocationState,
  manufacturerId,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userData } = useSelector((state) => state.user);

  const liveRates = useSelector((state) => state.liveRate.rates);

  const rateMap = useMemo(() => {
    return liveRates.reduce((acc, item) => {
      acc[item.metal?.toLowerCase()] = item.rate;
      return acc;
    }, {});
  }, [liveRates]);

  const goldRate = rateMap["gold"] || 0;
  const silverRate = rateMap["silver"] || 0;

  const lastUpdated = useSelector((state) => state.liveRate.lastUpdated);

  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    const updateCountdown = () => {
      const lastUpdated = parseInt(
        localStorage.getItem(LIVE_RATE_TIMESTAMP_KEY),
        10,
      );
      const now = Date.now();

      if (!isNaN(lastUpdated)) {
        const elapsed = Math.floor((now - lastUpdated) / 1000); // in seconds
        const remaining = Math.max(0, 300 - elapsed); // 5 minutes = 300 seconds
        setCountdown(remaining);

        if (remaining === 0) {
          fetchAndStoreLiveRates(); // ⬅️ Refresh the rate
        }
      } else {
        setCountdown(0);
      }
    };

    updateCountdown(); // Initial call

    const countdownInterval = setInterval(() => {
      updateCountdown();
    }, 1000); // ⏱ Every 1 sec

    return () => clearInterval(countdownInterval);
  }, [dispatch]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const s = (seconds % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const LIVE_RATE_TIMESTAMP_KEY = "liveRateLastUpdated";
  const normalizeName = (name) =>
    name?.toLowerCase().replace(/\s+/g, " ").trim();

  const fetchAndStoreLiveRates = () => {
    dispatch(
      getLiveRateAPI({}, (result) => {
        if (!result?.status || !result?.data) return;

        const data = result.data;

        const goldEntry = data.find(
          (item) => normalizeName(item.commodity) === "gold 24k",
        );

        const silverEntry = data.find(
          (item) => normalizeName(item.commodity) === "silver",
        );

        const updatedRates = [];

        if (goldEntry?.rate) {
          updatedRates.push({ metal: "Gold", rate: goldEntry.rate });
        }

        if (silverEntry?.rate) {
          updatedRates.push({ metal: "Silver", rate: silverEntry.rate });
        }

        if (updatedRates.length > 0) {
          dispatch(setLiveRates(updatedRates));
          localStorage.setItem("cachedLiveRates", JSON.stringify(updatedRates));
          localStorage.setItem(LIVE_RATE_TIMESTAMP_KEY, Date.now());
        }
      }),
    );
  };

  useEffect(() => {
    const cachedRates = localStorage.getItem("cachedLiveRates");
    if (cachedRates) {
      try {
        const parsed = JSON.parse(cachedRates);
        dispatch(setLiveRates(parsed)); // Restore Redux from cache
      } catch (e) {
        console.log("❌ Failed to parse cached live rates");
      }
    }
  }, [dispatch]);

  useEffect(() => {
    const now = Date.now();
    const lastUpdatedFromStorage = parseInt(
      localStorage.getItem(LIVE_RATE_TIMESTAMP_KEY),
      10,
    );

    const shouldUpdate =
      isNaN(lastUpdatedFromStorage) ||
      differenceInMinutes(new Date(now), new Date(lastUpdatedFromStorage)) >= 5;

    if (shouldUpdate) {
      // console.log("⏱ Fetching live rate from API");
      fetchAndStoreLiveRates();
    } else {
      // console.log("⏳ Using cached live rate - no fetch needed");
    }

    const interval = setInterval(
      () => {
        fetchAndStoreLiveRates();
      },
      5 * 60 * 1000,
    ); // Refresh every 5 min

    return () => clearInterval(interval);
  }, []);

  const getLiveRate = (metal) => {
    return rateMap[metal?.toLowerCase()] || 0;
  };

  const cartChangeTrigger = useSelector(
    (state) => state.popupTrigger.cartChangeTrigger,
  );

  const [totalAmountData, setTotalAmountData] = useState({});

  const [orderSummaryData, setOrderSummaryData] = useState([]);

  const [orderSummaryDataPerPcsProduct, setOrderSummaryDataPerPcsProduct] =
    useState([]);

  const [isAddAddressModal, setIsAddAddressModal] = useState(false);

  const [billingAddress, setBillingAddress] = useState(null);
  const [shippingAddress, setShippingAddress] = useState([]);

  const [userRole, setUserRole] = useState("");
  const [totalBalance, setTotalBalance] = useState(null);

  const isChecked = useSelector((state) => state.wallet.isChecked);

  const fetchCartSummary = useCallback(() => {
    dispatch(
      getCartAPI({}, (result) => {
        setTotalAmountData(result?.total_amounts);
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    fetchCartSummary();
  }, [fetchCartSummary, cartChangeTrigger]);

  useEffect(() => {
    dispatch(
      getCartTotalAPI({ manufacturer_id: manufacturerId }, (result) => {
        setOrderSummaryData(result?.data);
        setOrderSummaryDataPerPcsProduct(result?.data);
      }),
    );
    dispatch(
      getLoggedInUserDetailsAPI({}, (result) => {
        setUserRole(result?.data?.[0]?.user_role_name);
      }),
    );

    dispatch(
      getAddressLists({}, (result) => {
        setBillingAddress(result?.data?.billing_address || null);
        // setShippingAddress(result?.data?.shipping_address || []);
        const filteredShipping =
          result?.data?.shipping_address?.filter(
            (address) => address?.defaultaddress,
          ) || [];

        if (filteredShipping?.length === 0) {
          setShippingAddress(result?.data?.shipping_address);
        } else {
          setShippingAddress(filteredShipping);
        }

        //2 jagyaaaa change
      }),
    );
    // if (addressDataByLocationState) {
    //   setShippingAddress([addressDataByLocationState]); // Set it as default
    // } else {
    //   dispatch(
    //     getAddressLists({}, (result) => {
    //       console.log("Address list result:", result);
    //       setBillingAddress(result?.data?.billing_address || null);
    //       // setShippingAddress(result?.data?.shipping_address || []);
    //       const filteredShipping = result?.data?.shipping_address?.filter(
    //         (address) => address?.defaultAddress === 1
    //       ) || [];

    //       setShippingAddress(filteredShipping);
    //     })
    //   );
    // }
  }, [dispatch, cartChangeTrigger]);

  useEffect(() => {
    // const intervalId = setInterval(() => {
    dispatch(
      getLoggedInUserDetailsAPI({}, (result) => {
        setTotalBalance(result?.data?.[0]?.wallet_amount);
      }),
    );
    // }, 1000); // 1 second

    // return () => clearInterval(intervalId); // Cleanup
  }, [dispatch]);

  const toggleAddress = () => {
    setIsAddAddressModal((prev) => !prev);
  };

  const onEditAddressNavigate = (type, address) => {
    navigate("/my-account/edit-address", {
      state: {
        addressType: type,
        addressData: address,
        isCartScreenVisit: true,
      },
    });
  };

  const onFetchUserAddress = useCallback(() => {
    try {
      dispatch(
        getAddressLists({}, (result) => {
          setBillingAddress(result?.data?.billing_address || null);
          // setShippingAddress(result?.data?.shipping_address.filter((address)=> address?.defaultAddress == true) || []);
          const filteredShipping =
            result?.data?.shipping_address?.filter(
              (address) => address?.defaultaddress,
            ) || [];

          setShippingAddress(filteredShipping);
          refreshAddressInCart();
        }),
      );
    } catch (error) {}
  }, [dispatch]);

  const handleWalletCheckClick = (e) => {
    if (e.target.checked) {
      // Switch turned ON → set true
      dispatch(setWalletFlag(true));
    } else {
      // Switch turned OFF → reset (sets to initialState.isChecked = false)
      dispatch(resetWalletFlag());
    }

    const reqBody = {
      flag_wallet: !isChecked,
    };
    dispatch(applyWalletCheckAPI(reqBody, (result) => {}));
  };

  const formatNumber = useCallback((num) => {
    if (typeof num !== "number") {
      num = parseFloat(num);
      if (isNaN(num)) return num;
    }

    num = Math.ceil(num);

    return numberFormatter.format(Math.ceil(num));
  }, []);

  const isRetailer = userRole?.toLowerCase() === "retailer";

 const summaryCalculation = useMemo(() => {
   if (!orderSummaryData?.length) return null;

   const summary = orderSummaryData[0];

   const goldAmount = Number(summary.gold_amount || 0);
   const silverAmount = Number(summary.silver_amount || 0);

   const perPcGoldAmount = Number(
     summary?.per_pc_product_total?.gold?.amount || 0,
   );
   const perPcSilverAmount = Number(
     summary?.per_pc_product_total?.silver?.amount || 0,
   );

   const shippingAmount = Number(summary.shipping_amount || 0);

   const total =
     goldAmount +
     silverAmount +
     perPcGoldAmount +
     perPcSilverAmount +
     shippingAmount;

   const appliedWalletFund = isChecked ? Math.min(totalBalance || 0, total) : 0;

   return {
     goldAmount,
     silverAmount,
     perPcGoldAmount,
     perPcSilverAmount,
     shippingAmount,
     total,
     appliedWalletFund,
     finalTotal: total - appliedWalletFund,
   };
 }, [orderSummaryData, isChecked, totalBalance]);

  useEffect(() => {
    if (!summaryCalculation) return;

    dispatch(setFinalPayableAmount(summaryCalculation.finalTotal));
  }, [summaryCalculation, dispatch]);

  return (
    <div>
      {shippingAddress?.length > 0 ? (
        <AddressCard
          // isSameAddress
          shippingItem={shippingAddress}
          // shippingItem={shippingAddress?.slice(0, 1)}
          billingItem={billingAddress}
          onEditAddress={(type, address) =>
            onEditAddressNavigate(type, address)
          }
          isSameAddress={isSameAddress}
          setIsSameAddress={setIsSameAddress}
          // addressDataByLocationState={addressDataByLocationState}
        />
      ) : (
        <div
          style={{ cursor: "pointer" }}
          className="add-address-div-style"
          onClick={toggleAddress}
        >
          <img src={Icons.blackPlusIcon} alt="" className="add-icon" />
          <p>Add Address</p>
        </div>
      )}

      <div className="order-summary-main-div-class">
        <div className="left-side-div">
          <div className="h3-heading">{/* <h3>Order Summary</h3> */}</div>
          {orderSummaryData?.map((item, index) => {
            const gold = item?.product_total?.gold;
            const roseGold = item?.product_total?.["rose gold"];
            const silver = item?.product_total?.silver;

            const combinedGold = {
              net_weight:
                (Number(gold?.net_weight) || 0) +
                (Number(roseGold?.net_weight) || 0),
              gross_weight:
                (Number(gold?.gross_weight) || 0) +
                (Number(roseGold?.gross_weight) || 0),
              quantity:
                (Number(gold?.quantity) || 0) +
                (Number(roseGold?.quantity) || 0),
              fine: (Number(gold?.fine) || 0) + (Number(roseGold?.fine) || 0),
              stone_weight:
                (Number(gold?.stone_weight) || 0) +
                (Number(roseGold?.stone_weight) || 0),
              stone_amount:
                (Number(gold?.stone_amount) || 0) +
                (Number(roseGold?.stone_amount) || 0),
              labour_amount:
                (Number(gold?.labour_amount) || 0) +
                (Number(roseGold?.labour_amount) || 0),
              metal_amount:
                (Number(gold?.metal_amount) || 0) +
                (Number(roseGold?.metal_amount) || 0),
            };

            const summary = orderSummaryData[0];
            const productTotal = summary.product_total || {};
            const summaryRoseGold = productTotal["rose gold"];

            // GOLD CALCULATION (gold + rose gold combined)
            const goldFine =
              Number(productTotal.gold?.fine || 0) +
              Number(summaryRoseGold?.fine || 0);
            const goldRatePerGram = goldRate / 10;
            const goldAmount = goldFine * goldRatePerGram;

            // SILVER CALCULATION
            const silverFine = Number(productTotal.silver?.fine || 0);
            const silverRatePerGram = silverRate / 1000;
            const silverAmount = silverFine * silverRatePerGram;
            return (
              <div key={index} className="individual-prod-div">
                {/* GOLD BLOCK */}
                {combinedGold?.quantity > 0 && (
                  <>
                    <div
                      className="metal-name"
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <h3>Gold Products</h3>

                      <div
                        style={{
                          display: "flex",
                          gap: "20px",
                          backgroundColor: "black",
                          padding: "10px 16px",
                          borderRadius: "6px",
                        }}
                      >
                        <div>
                          <div
                            style={{
                              display: "flex",
                              gap: "8px",
                              alignItems: "center",
                              border: "1px solid white",
                              padding: "0px 10px",
                              borderRadius: "6px",
                            }}
                          >
                            <div
                              style={{
                                backgroundColor: "crimson",
                                borderRadius: "50%",
                                height: "10px",
                                width: "10px",
                              }}
                            ></div>
                            <p style={{ color: "white" }}>LIVE</p>
                          </div>
                        </div>
                        <p style={{ color: "white" }}>
                          Live Rate:
                          <span style={{ color: "red" }}>
                            {" "}
                            ₹{getLiveRate("Gold")}
                            /10g
                          </span>
                        </p>
                        <div>
                          <p style={{ color: "white" }}>
                            Valid For :{" "}
                            <span style={{ color: "red" }}>
                              ({formatTime(countdown)}){" "}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="inputss-div">
                      <div className="one-divv">
                        <div className="inp-label">
                          <p>Total Gross Weight</p>
                          <input
                            className="inp"
                            type="text"
                            value={combinedGold?.gross_weight}
                            readOnly
                          />
                        </div>
                        <div className="inp-label">
                          <p>Total Net Weight</p>
                          <input
                            className="inp"
                            type="text"
                            value={combinedGold?.net_weight}
                            readOnly
                          />
                        </div>
                      </div>

                      <div className="sec-divv">
                        <div className="inp-label">
                          <p>Gold Fine</p>
                          <input
                            className="inp"
                            type="text"
                            value={combinedGold?.fine}
                            readOnly
                          />
                        </div>
                        <div className="inp-label">
                          <p>Product Quantity</p>
                          <input
                            className="inp"
                            type="text"
                            value={combinedGold?.quantity}
                            readOnly
                          />
                        </div>
                      </div>

                      <div className="third-divv">
                        <div className="inp-label">
                          <p className="para">Gold Rate (10 gm)</p>
                          <input
                            className="inp"
                            type="text"
                            // value={item.gold_rate}
                            value={getLiveRate("Gold")}
                            readOnly
                          />
                        </div>
                      </div>
                      <div className="third-divv">
                        <div className="inp-label">
                          <p className="para">Gold Amount</p>
                          <input
                            className="inp"
                            type="text"
                            // value={orderSummaryData?.[0]?.gold_amount}
                            value={formatNumber(combinedGold?.metal_amount)}
                            readOnly
                          />
                        </div>
                      </div>
                      <div className="fourth-divv">
                        <div className="inp-label">
                          <p className="para">Stone weight</p>
                          <input
                            className="inp"
                            type="text"
                            // value={orderSummaryData?.[0]?.gold_amount}
                            value={combinedGold?.stone_weight}
                            readOnly
                          />
                        </div>
                      </div>
                      <div className="fourth-divv">
                        <div className="inp-label">
                          <p className="para">Stone Amount</p>
                          <input
                            className="inp"
                            type="text"
                            // value={orderSummaryData?.[0]?.gold_amount}
                            value={formatNumber(combinedGold?.stone_amount)}
                            readOnly
                          />
                        </div>
                      </div>
                      <div className="fourth-divv">
                        <div className="inp-label">
                          <p className="para">Labour Amount</p>
                          <input
                            className="inp"
                            type="text"
                            value={formatNumber(combinedGold?.labour_amount)}
                            readOnly
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* SILVER BLOCK */}
                {silver?.quantity > 0 && (
                  <>
                    <div
                      className="metal-name"
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginTop: "20px",
                      }}
                    >
                      <h3>Silver Products</h3>
                      <div
                        style={{
                          display: "flex",
                          gap: "20px",
                          background: "black",
                          padding: "8px 14px",
                          borderRadius: "6px",
                        }}
                      >
                        <p style={{ color: "white" }}>
                          Live Rate:
                          <span style={{ color: "red" }}>
                            {" "}
                            {/* ₹{item.silver_rate}/1kg */}₹
                            {getLiveRate("Silver")}/1kg
                          </span>
                        </p>
                      </div>
                    </div>

                    <div className="inputss-div">
                      <div className="one-divv">
                        <div className="inp-label">
                          <p>Total Gross Weight</p>
                          <input
                            className="inp"
                            type="text"
                            value={silver?.gross_weight}
                            readOnly
                          />
                        </div>
                        <div className="inp-label">
                          <p>Total Net Weight</p>
                          <input
                            className="inp"
                            type="text"
                            value={silver?.net_weight}
                            readOnly
                          />
                        </div>
                      </div>

                      <div className="sec-divv">
                        <div className="inp-label">
                          <p>Silver Fine</p>
                          <input
                            className="inp"
                            type="text"
                            value={silver?.fine}
                            readOnly
                          />
                        </div>
                        <div className="inp-label">
                          <p>Product Quantity</p>
                          <input
                            className="inp"
                            type="text"
                            value={silver?.quantity}
                            readOnly
                          />
                        </div>
                      </div>

                      <div className="third-divv">
                        <div className="inp-label">
                          <p className="para">Silver Rate (1 kg)</p>
                          <input
                            className="inp"
                            type="text"
                            // value={item.silver_rate}
                            value={getLiveRate("Silver")}
                            readOnly
                          />
                        </div>
                      </div>
                      <div className="third-divv">
                        <div className="inp-label">
                          <p className="para">Silver Amount</p>
                          <input
                            className="inp"
                            type="text"
                            // value={item.silver_rate}
                            value={formatNumber(silver?.metal_amount)}
                            readOnly
                          />
                        </div>
                      </div>
                      <div className="fourth-divv">
                        <div className="inp-label">
                          <p className="para">Stone weight</p>
                          <input
                            className="inp"
                            type="text"
                            value={silver?.stone_weight}
                            readOnly
                          />
                        </div>
                      </div>
                      <div className="fourth-divv">
                        <div className="inp-label">
                          <p className="para">Stone Amount</p>
                          <input
                            className="inp"
                            type="text"
                            value={formatNumber(silver?.stone_amount)}
                            readOnly
                          />
                        </div>
                      </div>
                      <div className="fourth-divv">
                        <div className="inp-label">
                          <p className="para">Labour Amount</p>
                          <input
                            className="inp"
                            type="text"
                            value={formatNumber(silver?.labour_amount)}
                            readOnly
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            );
          })}

          {/* PER PC PRODUCTS */}
          {orderSummaryDataPerPcsProduct?.map((item, index) => (
            <div className="individual-prod-div" key={index}>
              {/* GOLD PER PC PRODUCTS */}
              {item?.per_pc_product_total?.gold?.quantity > 0 && (
                <div>
                  <div className="metal-name">
                    <h3>Gold Per Pcs Products</h3>
                  </div>

                  <div className="inputss-div">
                    <div className="one-divv">
                      <div className="inp-label">
                        <p className="para">Product Quantity</p>
                        <input
                          className="inp"
                          readOnly
                          type="text"
                          value={item.per_pc_product_total.gold.quantity}
                        />
                      </div>
                    </div>

                    <div className="sec-divv">
                      <div className="inp-label">
                        <p className="para">Total Price</p>
                        <input
                          className="inp"
                          readOnly
                          type="text"
                          value={item.per_pc_product_total.gold.amount}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* SILVER PER PC PRODUCTS */}
              {item?.per_pc_product_total?.silver?.quantity > 0 && (
                <div>
                  <div className="metal-name">
                    <h3>Silver PCS Products</h3>
                  </div>

                  <div className="inputss-div">
                    <div className="one-divv">
                      <div className="inp-label">
                        <p className="para">Product Quantity</p>
                        <input
                          className="inp"
                          readOnly
                          type="text"
                          value={item.per_pc_product_total.silver.quantity}
                        />
                      </div>
                    </div>

                    <div className="sec-divv">
                      <div className="inp-label">
                        <p className="para">Total Price</p>
                        <input
                          className="inp"
                          readOnly
                          type="text"
                          value={item.per_pc_product_total.silver.amount}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
          {/* <p style={{ fontSize: "14px", color: "red", marginTop: "6px" }}>
            Note: Live rates refresh every 5 minutes and may vary.
          </p> */}
        </div>

        <div className="right-side-div">
          <div>
            <p className="h3-heading">Total Summary</p>
          </div>

          {summaryCalculation && (
            <div className="middle-div">
              {summaryCalculation.goldAmount +
                summaryCalculation.perPcGoldAmount >
                0 && (
                <div className="charges">
                  <p className="heading-light-text-class">
                    Approx Sub Total (Gold)
                  </p>
                  <span>
                    ₹
                    {formatNumber(
                      summaryCalculation.goldAmount +
                        summaryCalculation.perPcGoldAmount,
                    )}
                  </span>
                </div>
              )}

              {summaryCalculation.silverAmount +
                summaryCalculation.perPcSilverAmount >
                0 && (
                <div className="charges">
                  <p className="heading-light-text-class">
                    Approx Sub Total (Silver)
                  </p>
                  <span>
                    ₹
                    {formatNumber(
                      summaryCalculation.silverAmount +
                        summaryCalculation.perPcSilverAmount,
                    )}
                  </span>
                </div>
              )}

              <div className="charges">
                <p className="heading-light-text-class">Shipping Amount</p>
                <span>₹{formatNumber(summaryCalculation.shippingAmount)}</span>
              </div>

              {isChecked && summaryCalculation.appliedWalletFund > 0 && (
                <div className="charges">
                  <p className="heading-light-text-class">Wallet Applied</p>
                  <span style={{ color: "green" }}>
                    -₹{formatNumber(summaryCalculation.appliedWalletFund)}
                  </span>
                </div>
              )}

              <div className="charges total-amount">
                <p className="heading-bold-text-class">Total Amount</p>
                <span>₹{formatNumber(summaryCalculation.finalTotal)}/-</span>
              </div>
            </div>
          )}

          {Number(totalBalance) > 0 && (
            <div className="wallet-toggle">
              {Number(totalBalance) > 0 && (
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={handleWalletCheckClick} // ✅ single handler
                  />
                  <span className="slider round"></span>
                </label>
              )}

              <p className="wallet-text">
                {/* Use Funds from Wallet */}
                Use Wallet Balance
                {/* <span className="wallet-amount">₹{totalBalance}</span> */}
                <span className="wallet-amount">
                  ₹{formatNumber(totalBalance)}
                </span>{" "}
                available
              </p>
              {totalBalance <= 0 && (
                <p
                  className="add-fund-text"
                  onClick={() => navigate("/my-account/wallet")}
                >
                  Add Fund
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {isAddAddressModal && (
        <AddAddressModal
          onClose={() => setIsAddAddressModal(false)}
          onFetchUserAddress={onFetchUserAddress}
        />
      )}
    </div>
  );
};

export default RightSideCart;
